import { useRef, useEffect, useState, useMemo } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import * as THREE from 'three'
import DetailedCube from './BumpyCube'

function clamp(v: number, min: number, max: number) {
  return Math.min(max, Math.max(min, v))
}

function easeInOutCubic(t: number) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

function lerp3(a: THREE.Vector3, b: THREE.Vector3, t: number) {
  return new THREE.Vector3(
    a.x + (b.x - a.x) * t,
    a.y + (b.y - a.y) * t,
    a.z + (b.z - a.z) * t
  )
}

function useScrollFraction(): number {
  const [fraction, setFraction] = useState(0)
  useEffect(() => {
    function onScroll() {
      const scrollY = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const f = docHeight > 0 ? scrollY / docHeight : 0
      setFraction(clamp(f, 0, 1))
    }
    window.addEventListener('scroll', onScroll)
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  return fraction
}

function Connection({ start, end, opacity }: {
  start: [number, number, number]
  end: [number, number, number]
  opacity: number
}) {
  const points = [
    new THREE.Vector3(...start),
    new THREE.Vector3(...end),
  ]
  const geometry = new THREE.BufferGeometry().setFromPoints(points)
  return (
    <line geometry={geometry}>
      <lineBasicMaterial
        color="#93C5FD"
        linewidth={2}
        transparent
        opacity={opacity}
      />
    </line>
  )
}

function DataParticles() {
  const count = 50
  const particlesRef = useRef<THREE.Points>(null!)
  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3)
    for (let i = 0; i < count; i++) {
      pos[i * 3 + 0] = (Math.random() - 0.5) * 12
      pos[i * 3 + 1] = (Math.random() - 0.5) * 12
      pos[i * 3 + 2] = (Math.random() - 0.5) * 12
    }
    return pos
  }, [count])

  useFrame(() => {
    if (particlesRef.current) {
      particlesRef.current.rotation.y += 0.0005
    }
  })

  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.1}
        color="#60A5FA"
        sizeAttenuation
        transparent
        opacity={0.35}
      />
    </points>
  )
}

const keyframes = [
  { t: 0.0, cameraPos: new THREE.Vector3(-1.8, 2, 8.5), groupPos: new THREE.Vector3(3.5, 0, 0), groupScale: 0.4 },
  { t: 0.1, cameraPos: new THREE.Vector3(0, 3, 10), groupPos: new THREE.Vector3(0.5, 0, 0), groupScale: 0.6 },
  { t: 0.2, cameraPos: new THREE.Vector3(0, 5, 12), groupPos: new THREE.Vector3(0, 0, 0), groupScale: 0.8 },
  { t: 0.25, cameraPos: new THREE.Vector3(5, 6, 11), groupPos: new THREE.Vector3(0, 0, 0), groupScale: 1.1 },
  { t: 0.3, cameraPos: new THREE.Vector3(10, 8, 10), groupPos: new THREE.Vector3(0, 0, 0), groupScale: 1.4 },
  { t: 0.5, cameraPos: new THREE.Vector3(-1.8, 2, 8.5), groupPos: new THREE.Vector3(-2.5, 0, -5), groupScale: 0.55 },
  { t: 0.55, cameraPos: new THREE.Vector3(-6, 0, 7), groupPos: new THREE.Vector3(-1.5, 0, -3), groupScale: 0.85 },
  { t: 0.6, cameraPos: new THREE.Vector3(-11, -4, 6), groupPos: new THREE.Vector3(0, 0, 0), groupScale: 1 },
  { t: 0.7, cameraPos: new THREE.Vector3(-11, 0, 7.2), groupPos: new THREE.Vector3(0.7, 0, -1), groupScale: 1.3 },
  { t: 0.8, cameraPos: new THREE.Vector3(-11, 2, 8.5), groupPos: new THREE.Vector3(2.5, 0, 0), groupScale: 0.8 },
  { t: 0.9, cameraPos: new THREE.Vector3(-1.8, 2, 8.5), groupPos: new THREE.Vector3(0, 0, 0), groupScale: 0.55 },
  { t: 1.0, cameraPos: new THREE.Vector3(-1.8, 2, 8.5), groupPos: new THREE.Vector3(0, 0, 0), groupScale: 1 }
]

function getSceneValues(t: number) {
  if (t <= keyframes[0].t) {
    return {
      cameraPos: keyframes[0].cameraPos.clone(),
      groupPos: keyframes[0].groupPos.clone(),
      groupScale: keyframes[0].groupScale,
    }
  }
  if (t >= keyframes[keyframes.length - 1].t) {
    const last = keyframes[keyframes.length - 1]
    return {
      cameraPos: last.cameraPos.clone(),
      groupPos: last.groupPos.clone(),
      groupScale: last.groupScale,
    }
  }
  for (let i = 0; i < keyframes.length - 1; i++) {
    const kA = keyframes[i]
    const kB = keyframes[i + 1]
    if (t >= kA.t && t <= kB.t) {
      const localRange = kB.t - kA.t
      const localT = (t - kA.t) / localRange
      const eased = easeInOutCubic(localT)
      const cameraPos = lerp3(kA.cameraPos, kB.cameraPos, eased)
      const groupPos = lerp3(kA.groupPos, kB.groupPos, eased)
      const groupScale = kA.groupScale + (kB.groupScale - kA.groupScale) * eased
      return { cameraPos, groupPos, groupScale }
    }
  }
  return {
    cameraPos: keyframes[0].cameraPos.clone(),
    groupPos: keyframes[0].groupPos.clone(),
    groupScale: keyframes[0].groupScale,
  }
}

function Scene({ scrollT }: { scrollT: number }) {
  const { camera } = useThree()
  const groupRef = useRef<THREE.Group>(null!)
  const controlsRef = useRef<any>(null!)
  const basePositions: [number, number, number][] = [
    [-6, 0, 0],
    [0, 6, 0],
    [6, 0, 0],
    [0, -6, 0],
    [-4, 4, 4],
    [4, 4, -4],
    [-4, -4, -4],
    [4, -4, 4],
  ]

  useFrame(() => {
    const { cameraPos, groupPos, groupScale } = getSceneValues(scrollT)
    camera.position.lerp(cameraPos, 0.1)
    camera.lookAt(0, 0, 0)
    groupRef.current.position.lerp(groupPos, 0.1)
    groupRef.current.scale.lerp(
      new THREE.Vector3(groupScale, groupScale, groupScale),
      0.1
    )

    if (scrollT < 0.2) {
      groupRef.current.rotation.y += 0.01
    }
    if (scrollT > 0.2 && scrollT < 0.75) {
      groupRef.current.rotation.y += 0.01
      groupRef.current.rotation.x += 0.01
    }
    if (scrollT > 0.8) {
      groupRef.current.rotation.y += 0.01
    }
  })

  return (
    <group ref={groupRef}>
      <OrbitControls
        ref={controlsRef}
        enableRotate={scrollT < 0.2}
        enableZoom={scrollT < 0.2}
        enablePan={false}
      />
      <DataParticles />
      {basePositions.map((pos, i) => (
        <DetailedCube key={i} position={pos} />
      ))}
      {basePositions.map((start, i) => {
        const end = basePositions[(i + 1) % basePositions.length]
        return (
          <Connection
            key={i}
            start={start}
            end={end}
            opacity={0.5}
          />
        )
      })}
    </group>
  )
}


function BackgroundParticles() {
  const { camera } = useThree()
  const particlesRef = useRef<THREE.Points>(null!)
  const particlesCount = 2000
  const posArray = useMemo(() => {
    const positions = new Float32Array(particlesCount * 3)
    for (let i = 0; i < particlesCount; i++) {
      positions[i * 3 + 0] = (Math.random() - 0.5) * 10
      positions[i * 3 + 1] = (Math.random() - 0.5) * 10
      positions[i * 3 + 2] = (Math.random() - 0.5) * 10
    }
    return positions
  }, [particlesCount])

  useFrame(() => {
    if (particlesRef.current) {
      particlesRef.current.rotation.y += 0.001
      particlesRef.current.position.copy(camera.position)
    }
  })

  return (
    <points ref={particlesRef} renderOrder={-1}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={posArray.length / 3}
          array={posArray}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.005}
        color={0x02A9F7}
        transparent
        opacity={0.8}
        depthTest={false}
      />
    </points>
  )
}

export default function CombinedBlockchainScene() {
  const scrollT = useScrollFraction()

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10,
        pointerEvents: scrollT < 0.2 ? 'auto' : 'none',
      }}
    >
      <Canvas camera={{ fov: 50, near: 0.1, far: 1000 }} gl={{ alpha: true }}>
        <color attach="background" args={['#0A0A0B']} />
        <ambientLight intensity={0.4} />
        <pointLight position={[10, 10, 10]} intensity={1} />
        <pointLight position={[-10, -10, -10]} intensity={0.6} />
        <BackgroundParticles />
        <Scene scrollT={scrollT} />
      </Canvas>
    </div>
  )
}
